<template>
  <common-dialog
    :is-open="isOpen"
    :header-text="$t('workPackages.copyOf', { workpackageName: options.source.description })"
    :ok-btn-text="$t('actions.confirmCopy')"
    :ok-btn-disabled="!isValid"
    @onOk="emitOk"
    @onCancel="emitCancel"
  >
    <template #body>
      <v-row no-gutters>
        <v-col cols="12">
          <v-text-field
            v-model.trim="targetWorkpackage.description"
            :label="$t('workPackages.enterWorkpackageName')"
            :rules="[valueRequired]"
          />
        </v-col>
      </v-row>
      <v-row>
        <feature-toggle toggle="enableReferenceDate">
          <v-col cols="6" class="pt-0 pb-0">
            <v-menu
              v-model="referenceDatePicker"
              :close-on-content-click="false"
              :nudge-right="5"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="computedReferenceDate"
                  :label="$t('workPackages.referenceDate')"
                  :disabled="isExactCopy"
                  append-icon="event"
                  readonly
                  clearable
                  v-on="on"
                  @click:append="referenceDatePicker = true"
                  @click:clear="targetWorkpackage.referenceDate = null"
                />
              </template>
              <v-date-picker
                v-model="targetWorkpackage.referenceDate"
                :first-day-of-week="i18nconfig.firstDayOfTheWeek"
                :locale="i18nconfig.fallbackLocale"
                :min="sixMonthsAgo"
                :max="todaysDate"
                @input="referenceDatePicker = false"
              />
            </v-menu>
          </v-col>
        </feature-toggle>
        <v-col cols="6" class="pt-0 pb-0">
          <v-menu
            v-model="completionDatePicker"
            :close-on-content-click="false"
            :nudge-right="5"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="computedCompletionDate"
                :label="$t('workPackages.completionDate')"
                :disabled="isExactCopy"
                append-icon="event"
                readonly
                v-on="on"
                @click:append="completionDatePicker = true"
              />
            </template>
            <v-date-picker
              v-model="targetWorkpackage.completionDate"
              :first-day-of-week="i18nconfig.firstDayOfTheWeek"
              :locale="i18nconfig.fallbackLocale"
              @input="completionDatePicker = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="6" class="pt-0 pb-0">
          <v-menu
            v-model="goLiveDatePicker"
            :close-on-content-click="false"
            :nudge-right="5"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="computedGoLiveDate"
                :label="$t('workPackages.goLiveDate')"
                append-icon="event"
                readonly
                v-on="on"
                @click:append="goLiveDatePicker = true"
              />
            </template>
            <v-date-picker
              v-model="targetWorkpackage.goLiveDate"
              :first-day-of-week="i18nconfig.firstDayOfTheWeek"
              :locale="i18nconfig.fallbackLocale"
              @input="onGoLiveDateInput()"
            />
          </v-menu>
        </v-col>
      </v-row>

      <!-- Cost Threshold Min/Max (Competitor Simulation) -->
      <cost-threshold-inputs
        :workpackage="targetWorkpackage"
        :disabled="isExactCopy"
        @validation-change="hasThresholdValidationError = $event"
      />

      <!-- Workpackage Period Type (weekly / aoPeriod) -->
      <feature-toggle toggle="enableWorkpackagePeriodTypes">
        <v-row no-gutters>
          <v-col cols="6">
            <v-select
              v-model="targetWorkpackage.workpackagePeriodType"
              :items="workpackagePeriodTypeItems"
              :label="`${$t('workPackages.workpackagePeriodType')} *`"
              :rules="[valueRequired]"
              :disabled="isExactCopy"
              item-text="text"
              item-value="value"
            />
          </v-col>
        </v-row>
      </feature-toggle>

      <feature-toggle toggle="enableAOExtracts">
        <v-row no-gutters>
          <v-col cols="12">
            <div>{{ $t('workPackages.aoExtracts') }}</div>
            <v-radio-group
              v-model="options.reuseExistingAOExtracts"
              :disabled="isExactCopy"
              hide-details
            >
              <v-radio
                :value="true"
                :label="$t('workPackages.aoExtractsSelection.copyFromExisting')"
              />
              <v-radio
                :value="false"
                :label="$t('workPackages.aoExtractsSelection.selectNewSource')"
              />
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row v-if="!options.reuseExistingAOExtracts" no-gutters>
          <v-col cols="12">
            <div class="ao-extracts">
              <v-autocomplete
                v-model="options.selectedAOExtracts"
                :items="aoExtractsOptions"
                multiple
                dense
                item-text="displayName"
                item-value="auditCorrelationId"
                :rules="[aoExtractsRules]"
              />
              <feature-toggle toggle="usePeriodIdsInAOExtracts">
                <template v-if="options.selectedAOExtracts.length > 0">
                  <div class="mb-2">{{ $t('workPackages.associatedPeriodIds') }}</div>
                  <div>{{ getAssociatedPeriodIds(options.selectedAOExtracts) }}</div>
                </template>
              </feature-toggle>
            </div>
          </v-col>
        </v-row>
      </feature-toggle>

      <exact-copy-toggle
        :is-exact-copy="isExactCopy"
        :disabled="!goLiveDatesMatch"
        @change="userDisabledExactCopy = !$event"
      />

      <optimise-prices-toggle :force-disabled="isExactCopy" />
      <refresh-on-create-toggle :force-disabled="isExactCopy" />
    </template>
  </common-dialog>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import {
  enableAOExtracts,
  usePeriodIdsInAOExtracts,
  enableCalculatedCompletionDate,
  enableCompetitorSimulation,
  defaultCostChangeThresholdMin,
  defaultCostChangeThresholdMax,
  enableWorkpackagePeriodTypes,
} from '@enums/feature-flags';
import workpackagePeriodTypes from '@enums/workpackage-period-types';
import { yearMonthDayFormat } from '@enums/date-formats';
import featureFlagsMixin from '../../../mixins/featureFlags';
import costThresholdInputs from '../../../components/cost-threshold-inputs.vue';

const todaysDate = moment().format('YYYY-MM-DD');
const sixMonthsAgo = moment()
  .subtract(6, 'months')
  .format('YYYY-MM-DD');

export default {
  components: {
    costThresholdInputs,
  },
  mixins: [featureFlagsMixin],
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      enableAOExtracts,
      usePeriodIdsInAOExtracts,
      hasThresholdValidationError: false,
      referenceDatePicker: false,
      completionDatePicker: false,
      goLiveDatePicker: false,
      todaysDate,
      sixMonthsAgo,
      userDisabledExactCopy: false,
    };
  },

  computed: {
    ...mapState('clientConfig', ['i18nconfig']),
    ...mapGetters('workpackages', [
      'getAvailableAOExtracts',
      'getAssociatedPeriodIds',
      'isAOExtractsValid',
      'calculateCompletionDate',
    ]),

    // Exact Copy is active when go-live dates match and the user hasn't manually disabled it.
    isExactCopy() {
      return this.goLiveDatesMatch && !this.userDisabledExactCopy;
    },

    goLiveDatesMatch() {
      return this.targetWorkpackage.goLiveDate === get(this.options.source, 'goLiveDate');
    },

    targetWorkpackage() {
      return this.options.target;
    },

    isValid() {
      const hasValidWorkpackagePeriodType =
        !this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes) ||
        this.isExactCopy ||
        !isEmpty(this.targetWorkpackage.workpackagePeriodType);

      return (
        !isEmpty(get(this.targetWorkpackage, 'description', '').trim()) &&
        hasValidWorkpackagePeriodType &&
        this.isAOExtractsValid(this.options.selectedAOExtracts) &&
        !this.hasThresholdValidationError
      );
    },

    computedReferenceDate() {
      return this.formatLocalisedDate(this.targetWorkpackage.referenceDate);
    },

    computedCompletionDate() {
      return this.formatLocalisedDate(this.targetWorkpackage.completionDate);
    },

    computedGoLiveDate() {
      return this.formatLocalisedDate(this.targetWorkpackage.goLiveDate);
    },

    aoExtractsOptions() {
      return this.getAvailableAOExtracts(this.targetWorkpackage.goLiveDate);
    },

    workpackagePeriodTypeItems() {
      return [
        { text: this.$t('workPackages.periodTypes.weekly'), value: workpackagePeriodTypes.weekly },
        {
          text: this.$t('workPackages.periodTypes.aoPeriod'),
          value: workpackagePeriodTypes.aoPeriod,
        },
      ];
    },

    aoExtractsRules() {
      return (
        this.isAOExtractsValid(this.options.selectedAOExtracts) ||
        this.$t('validationErrors.periodIdsDatesDoNotMatch')
      );
    },
  },

  watch: {
    isExactCopy(value) {
      if (value) {
        this.restoreSourceValues();
      } else {
        this.defaultCreateWorkpackageOptions();
      }
    },

    'targetWorkpackage.goLiveDate': function(value) {
      this.userDisabledExactCopy = false;
      const sourceGoLiveDate = get(this.options.source, 'goLiveDate');
      if (value !== sourceGoLiveDate) {
        this.setCreateWorkpackageRefreshFromFeed(true);
        // Reset cost thresholds to defaults when go-live date differs from source
        if (this.isFeatureFlagEnabled(enableCompetitorSimulation)) {
          this.$set(this.targetWorkpackage, 'costChangeThresholds', {
            min: this.toggleLogic[defaultCostChangeThresholdMin],
            max: this.toggleLogic[defaultCostChangeThresholdMax],
          });
        }
        // Auto-select weekly when go-live date changes on copy
        if (this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes)) {
          this.$set(this.targetWorkpackage, 'workpackagePeriodType', workpackagePeriodTypes.weekly);
        }
      } else {
        this.restoreSourceValues();
      }
      // set completion date based on go-live date if feature flag is enabled
      // match getCompletionDateBasedOnGoLiveDate in webtool/client/src/js/pages/home/components/work-packages.vue
      if (this.isFeatureFlagEnabled(enableCalculatedCompletionDate)) {
        this.targetWorkpackage.completionDate = this.calculateCompletionDate({ goLiveDate: value });
      }
    },
    'targetWorkpackage.referenceDate': function(value) {
      // if sourceWorkpackage has not been selected but date changed, still set refresh flag
      const sourceReferenceDate = get(this.options.source, 'referenceDate');
      if (value !== sourceReferenceDate) {
        this.setCreateWorkpackageRefreshFromFeed(true);
      }
    },
  },

  methods: {
    ...mapMutations('workpackages', [
      'setCreateWorkpackageRefreshFromFeed',
      'defaultCreateWorkpackageOptions',
      'setExactCopyWorkpackageOptions',
    ]),

    emitOk() {
      this.$emit('onOk', { isExactCopy: this.isExactCopy });
    },

    emitCancel() {
      this.$emit('onCancel');
    },

    onGoLiveDateInput() {
      this.goLiveDatePicker = false;
      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        this.options.selectedAOExtracts = [];
      }
    },

    valueRequired(v) {
      return !!v || v === 0 || this.$t('settings.parameters.errors.fieldInvalid');
    },

    restoreSourceValues() {
      this.setExactCopyWorkpackageOptions();
      this.$set(
        this.targetWorkpackage,
        'referenceDate',
        get(this.options.source, 'referenceDate', null)
      );
      const sourceCompletionDate = get(this.options.source, 'completionDate', null);
      this.$set(
        this.targetWorkpackage,
        'completionDate',
        sourceCompletionDate ? moment(sourceCompletionDate).format(yearMonthDayFormat) : null
      );
      if (this.isFeatureFlagEnabled(enableCompetitorSimulation)) {
        this.$set(this.targetWorkpackage, 'costChangeThresholds', {
          min: get(this.options.source, 'costChangeThresholds.min', 0),
          max: get(this.options.source, 'costChangeThresholds.max', 0),
        });
      }
      if (this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes)) {
        this.$set(
          this.targetWorkpackage,
          'workpackagePeriodType',
          get(this.options.source, 'workpackagePeriodType', null)
        );
      }
      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        this.options.reuseExistingAOExtracts = true;
      }
    },
  },
};
</script>
